import React from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSearchParams } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { checkAndUpdateOrderProgress, saveOrderDraft } from "../../utils/firebaseUtils";
import { useSnackbar } from "notistack";

type Field = {
  name: string;
  label: string;
  dateName?: string;
};

const relations = [
  "Featured husband",
  "Featured wife (maiden name)",
  "Couple's 1st child",
  "Couple's 2nd child",
  "Couple's 3rd child",
  "Couple's 4th child",
  "Couple's 5th child",
  "Couple's 6th child",
  "Husband's father",
  "Husband's mother (maiden name)",
  "Wife's father",
  "Wife's mother (maiden name)",
  "Husband's father's father",
  "Husband's father's mother (maiden name)",
  "Husband's mother's father",
  "Husband's mother's mother (maiden name)",
  "Wife's father's father",
  "Wife's father's mother (maiden name)",
  "Wife's mother's father",
  "Wife's mother's mother (maiden name)",
  "Husband's father's paternal grandfather",
  "Husband's father's paternal grandmother (maiden name)",
  "Husband's father's maternal grandfather",
  "Husband's father's maternal grandmother (maiden name)",
  "Husband's mother's paternal grandfather",
  "Husband's mother's paternal grandmother (maiden name)",
  "Husband's mother's maternal grandfather",
  "Husband's mother's maternal grandmother (maiden name)",
  "Wife's father's paternal grandfather",
  "Wife's father's paternal grandmother (maiden name)",
  "Wife's father's maternal grandfather",
  "Wife's father's maternal grandmother (maiden name)",
  "Wife's mother's paternal grandfather",
  "Wife's mother's paternal grandmother (maiden name)",
  "Wife's mother's maternal grandfather",
  "Wife's mother's maternal grandmother (maiden name)",
];

const fields: Field[] = [
  { name: "yourName", label: "Your Name*" },
  { name: "familyTreeLayout", label: "Family Tree Layout you are ordering*" },
  { name: "instructions", label: "Instructions from you to us*" },
  ...Array.from({ length: relations.length }, (_, i) => ({
    name: `relation_${i}_name`,
    label: `Relation ${i + 1} Name`,
    dateName: `relation_${i}_birthdate`,
  })),
];

const FamilyNamesForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const [selectedDates, setSelectedDates] = React.useState<{
    [key: string]: any;
  }>({});
  const [formData, setFormData] = React.useState(() => {
    const initialData: { [key: string]: any } = {};
    fields.forEach((field) => {
      initialData[field.name] = "";
      if (field.dateName) {
        initialData[field.dateName] = null; // Initialize date fields as null
      }
    });
    return initialData;
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitStatus, setSubmitStatus] = React.useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const { enqueueSnackbar } = useSnackbar();
  
  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (key: string, newValue: any) => {
    setSelectedDates((prev) => ({ ...prev, [key]: newValue }));
  };

  const validateForm = () => {
    const requiredFields = ["yourName", "familyTreeLayout", "instructions"];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (missingFields.length > 0) {
      return `Please fill in all required fields: ${missingFields
        .map((f) => fields.find((field) => field.name === f)?.label || f)
        .join(", ")}`;
    }

    // Check if at least one relation is filled or one misc is filled
    const hasFamilyMembers =
      Object.keys(formData).some(
        (key) =>
          (key.startsWith("relation_") || key.startsWith("misc_")) &&
          formData[key] &&
          formData[key].trim() !== ""
      ) ||
      Object.keys(selectedDates).some(
        (key) => selectedDates[key] !== null
      );

    if (!hasFamilyMembers) {
      return "Please add at least one family member detail or birthdate.";
    }

    return null;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orderId) {
      setSubmitStatus({
        type: "error",
        message:
          "No order ID provided. Please access this form from your order page.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setSubmitStatus({ type: "error", message: validationError });
      setIsSubmitting(false);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      // Combine formData with selectedDates into one object
      const dataToSubmit = {
        ...formData,
        ...selectedDates,
        submittedAt: new Date().toISOString(),
      };

      // Save form data to Firestore
      const orderRef = doc(db, "orders", orderId);
      await updateDoc(orderRef, {
        "formData.FamilyTree": dataToSubmit,
        "formStatus.FamilyTree": true,
        lastFormUpdate: serverTimestamp(),
      });

      // Check if this triggers a status update
      const updatedFormStatus = { FamilyTree: true };
      await checkAndUpdateOrderProgress(orderId, updatedFormStatus);

      setSubmitStatus({
        type: "success",
        message:
          "Family tree information saved successfully! Your order status has been updated.",
      });


    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus({
        type: "error",
        message: "Failed to save form data. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!orderId) {
      enqueueSnackbar("No order ID found. Cannot save draft.", { variant: "error" });
      return;
    }

    setIsSubmitting(true);
    try {
        // Combine formData with selectedDates into one object
      const dataToSubmit = {
        ...formData,
        ...selectedDates,
        lastSavedAt: new Date().toISOString(),
        isDraft: true
      };

      await saveOrderDraft(orderId, "FamilyTree", dataToSubmit);
      enqueueSnackbar("Draft saved successfully!", { variant: "success" });
    } catch (error) {
      console.error("Error saving draft:", error);
      enqueueSnackbar("Failed to save draft.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{ maxWidth: "900px", margin: "auto", mt: 4, mb: 4 }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Typography variant="h4" gutterBottom>
          Family Tree Names & Birthdates
        </Typography>
        <Typography variant="body1" paragraph>
          Fill out the appropriate fields which correspond with your chosen
          Family Tree layout. Use the misc. fields if we have not provided
          enough fields to include all the members of your family (for example:
          if you have more than 6 children).
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="h6" component="label">
              Your Name*
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              name="yourName"
              variant="outlined"
              required
              onChange={handleInputChange}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="h6" component="label">
              Family Tree Layout you are ordering*
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              name="familyTreeLayout"
              variant="outlined"
              required
              onChange={handleInputChange}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="h6" component="label">
              Instructions from you to us: title, quote, decorative corners,
              custom requests, comments, etc.*
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              name="instructions"
              variant="outlined"
              required
              onChange={handleInputChange}
            />
          </Grid>

          <Divider sx={{ width: "100%", mt: 4, mb: 4 }} />

          {[
            "Featured husband",
            "Featured wife (maiden name)",
            "Couple's 1st child",
            "Couple's 2nd child",
            "Couple's 3rd child",
            "Couple's 4th child",
            "Couple's 5th child",
            "Couple's 6th child",
            "Husband's father",
            "Husband's mother (maiden name)",
            "Wife's father",
            "Wife's mother (maiden name)",
            "Husband's father's father",
            "Husband's father's mother (maiden name)",
            "Husband's mother's father",
            "Husband's mother's mother (maiden name)",
            "Wife's father's father",
            "Wife's father's mother (maiden name)",
            "Wife's mother's father",
            "Wife's mother's mother (maiden name)",
            "Husband's father's paternal grandfather",
            "Husband's father's paternal grandmother (maiden name)",
            "Husband's father's maternal grandfather",
            "Husband's father's maternal grandmother (maiden name)",
            "Husband's mother's paternal grandfather",
            "Husband's mother's paternal grandmother (maiden name)",
            "Husband's mother's maternal grandfather",
            "Husband's mother's maternal grandmother (maiden name)",
            "Wife's father's paternal grandfather",
            "Wife's father's paternal grandmother (maiden name)",
            "Wife's father's maternal grandfather",
            "Wife's father's maternal grandmother (maiden name)",
            "Wife's mother's paternal grandfather",
            "Wife's mother's paternal grandmother (maiden name)",
            "Wife's mother's maternal grandfather",
            "Wife's mother's maternal grandmother (maiden name)",
          ].map((relation, index) => (
            <React.Fragment key={index}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1" component="label">
                  {relation} & birthdate
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  name={`relation_${index}_name`}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <DatePicker
                  label="Birthdate"
                  value={selectedDates[`relation_${index}_birthdate`]}
                  onChange={(newValue) =>
                    handleDateChange(`relation_${index}_birthdate`, newValue)
                  }
                />
              </Grid>
            </React.Fragment>
          ))}

          {[0, 1, 2, 3, 4, 5, 6].map((index) => (
            <React.Fragment key={index}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1" component="label">
                  misc/you label them/name & birthdate
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  name={`misc_${index}_name`}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <DatePicker
                  label="Birthdate"
                  value={selectedDates[`misc_${index}_birthdate`]}
                  onChange={(newValue) =>
                    handleDateChange(`misc_${index}_birthdate`, newValue)
                  }
                />
              </Grid>
            </React.Fragment>
          ))}

          <Grid size={{ xs: 12 }}>
            {submitStatus.type && (
              <Alert severity={submitStatus.type} sx={{ mb: 2 }}>
                {submitStatus.message}
              </Alert>
            )}

            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={isSubmitting || !orderId}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? "Saving..." : "Submit Family Tree Information"}
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              disabled={isSubmitting || !orderId}
              onClick={handleSaveDraft}
              sx={{ mt: 2 }}
            >
              Save Draft (Finish Later)
            </Button>

            {!orderId && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please access this form from your order page to ensure proper
                data saving.
              </Alert>
            )}
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default FamilyNamesForm;
